/**
 * Maps a raw `launcher_sfdc_campaign_members.cm_status` value (the real
 * vocabulary is `CAMPAIGN_STATUS_ORDER`, `src/shared/schemas/terra-signals.ts`)
 * to a Glaze `StatusBadge` status + display label.
 *
 * Shared by U3 (Custom Invite campaign history list, Item E) and U10 (the
 * `CampaignMemberSidebar` panel) per plan OQ4 — created once here in U9's
 * wave so both later units import it rather than each declaring their own
 * copy of the same mapping.
 *
 * `null` means Terra has invited the contact but no SFDC Campaign Member row
 * has synced back yet ("Invited (Terra)" — distinct from the real SFDC
 * `'Invited'` status, which only exists once a CM row exists).
 *
 * Reuse-before-build note (`.claude/team-conventions.md`): checked
 * `mini-apps/playbooks/subtype-badge-colors.ts` first. It maps campaign
 * *subtype* (Livestream/Webinar/...) to a `BadgeColor` hue, and separately
 * maps `Campaign.launcher_status` (`'draft' | 'launched'`, a two-value
 * Terra-internal enum) to a `StatusBadgeStatus`. Neither covers `cm_status`
 * (the ~13-value `CAMPAIGN_STATUS_ORDER` vocabulary this module maps) — a
 * different column, a different domain, no overlap to reuse. This module is
 * net new rather than an extension of that file.
 *
 * Type-only import from `@clay-run/glaze` (no runtime dependency on
 * Glaze/React), same pattern as `subtype-badge-colors.ts`.
 */
import type { StatusBadgeStatus } from '@clay-run/glaze';

const SUCCESS_STATUSES = new Set<string>(['Registered', 'Attended', 'Responded', 'Approved', 'Met with']);
const ACTION_STATUSES = new Set<string>(['Invited', 'Sent', 'HiL/Awaiting', 'Viewed replay']);
const WARNING_STATUSES = new Set<string>(['Pending approval']);
const ERROR_STATUSES = new Set<string>(['Declined', 'No-show']);
// `Suggested` and any unrecognized/future status both fall through to the
// `neutral` default below — no explicit set needed for either.

export interface CampaignStatusBadge {
  status: StatusBadgeStatus;
  label: string;
}

/**
 * `cmStatus` is the raw `launcher_sfdc_campaign_members.cm_status` column
 * value. `null` = no CM row synced yet (Terra invited the contact locally).
 */
export function campaignStatusBadge(cmStatus: string | null): CampaignStatusBadge {
  if (cmStatus === null) return { status: 'action', label: 'Invited (Terra)' };
  if (SUCCESS_STATUSES.has(cmStatus)) return { status: 'success', label: cmStatus };
  if (ACTION_STATUSES.has(cmStatus)) return { status: 'action', label: cmStatus };
  if (WARNING_STATUSES.has(cmStatus)) return { status: 'warning', label: cmStatus };
  if (ERROR_STATUSES.has(cmStatus)) return { status: 'error', label: cmStatus };
  return { status: 'neutral', label: cmStatus };
}
